import { useState } from 'react'
import { useAuth } from 'ui/components/AuthContext/AuthContext'

function getAttributesStorageKey(userId: number): string {
  return `appliedSettingValues_propertyChips_${userId}`
}

function getExpandedStorageKey(userId: number): string {
  return `appliedSettingValues_propertyChipsExpanded_${userId}`
}

function readStored(userId: number | undefined, key: (userId: number) => string): unknown {
  if (userId === undefined) {
    return undefined
  }

  try {
    return JSON.parse(localStorage.getItem(key(userId)) ?? 'null')
  } catch {
    return undefined
  }
}

function writeStored(userId: number | undefined, key: (userId: number) => string, value: unknown): void {
  if (userId === undefined) {
    return
  }

  try {
    localStorage.setItem(key(userId), JSON.stringify(value))
  } catch {
    // Ignore storage write errors (for example restricted browser storage mode).
  }
}

function readAttributes(userId: number | undefined): string[] {
  const stored = readStored(userId, getAttributesStorageKey)
  return Array.isArray(stored) ? stored.filter((attribute) => typeof attribute === 'string') : []
}

function readIsExpanded(userId: number | undefined): boolean {
  return readStored(userId, getExpandedStorageKey) !== false
}

type RememberedPropertyChips = {
  attributes: string[]
  setAttributes: (attributes: string[]) => void
  isExpanded: boolean
  setIsExpanded: (isExpanded: boolean) => void
}

/** The property chips the user has pressed, and whether the chip row is open, kept in this browser per user so they survive a reload. */
export function useRememberedPropertyChips(): RememberedPropertyChips {
  const userId = useAuth().user?.id
  const [state, setState] = useState(() => ({
    userId,
    attributes: readAttributes(userId),
    isExpanded: readIsExpanded(userId),
  }))

  if (state.userId !== userId) {
    setState({ userId, attributes: readAttributes(userId), isExpanded: readIsExpanded(userId) })
  }

  function setAttributes(attributes: string[]): void {
    setState({ ...state, userId, attributes })
    writeStored(userId, getAttributesStorageKey, attributes)
  }

  function setIsExpanded(isExpanded: boolean): void {
    setState({ ...state, userId, isExpanded })
    writeStored(userId, getExpandedStorageKey, isExpanded)
  }

  return { attributes: state.attributes, setAttributes, isExpanded: state.isExpanded, setIsExpanded }
}
